import TextField from '@mui/material/TextField';
import { ariaDescribedByIds, examplesId, getInputProps, labelValue } from '@rjsf/utils';
import { FormattedMessage } from 'react-intl';
import { digitsFaToEn, numberToWords } from '@persian-tools/persian-tools';
import { InputAdornment, Typography } from '@mui/material';

const TYPES_THAT_SHRINK_LABEL = ['date', 'datetime-local', 'file', 'time'];

/** The `BaseInputTemplate` is the template to use to render the basic `<input>` component for the `core` theme.
 * It is used as the template for rendering many of the <input> based widgets that differ by `type` and callbacks only.
 * It can be customized/overridden for other themes or individual implementations as needed.
 *
 * @param props - The `WidgetProps` for this template
 */
export default function BaseInputTemplate(props) {
  const {
    id,
    name, // remove this from textFieldProps
    placeholder,
    required,
    readonly,
    disabled,
    type,
    label,
    hideLabel,
    hideError,
    value,
    onChange,
    onChangeOverride,
    onBlur,
    onFocus,
    autofocus,
    options,
    schema,
    uiSchema,
    rawErrors = [],
    errorSchema,
    formContext,
    registry,
    InputLabelProps,
    ...textFieldProps
  } = props;
  const inputProps = getInputProps(schema, type, options);
  const { adornment, convertToWord = false } = options;

  // Now we need to pull out the step, min, max into an inner `inputProps` for material-ui
  const { step, min, max, ...rest } = inputProps;
  const otherProps = {
    inputProps: {
      step,
      min,
      max,
      ...(schema.examples ? { list: examplesId(id) } : undefined),
    },
    ...rest,
  };
  const _onChange = ({ target: { value } }) => onChange(value === '' ? options.emptyValue : digitsFaToEn(value));
  const _onBlur = ({ target: { value } }) => onBlur(id, value);
  const _onFocus = ({ target: { value } }) => onFocus(id, value);
  const DisplayInputLabelProps = TYPES_THAT_SHRINK_LABEL.includes(type)
    ? {
        ...InputLabelProps,
        shrink: true,
      }
    : InputLabelProps;

  const dir = inputProps?.type === 'number' ? 'ltr' : 'rtl';
  return (
    <>
      <TextField
        id={id}
        name={id}
        dir={dir}
        placeholder={placeholder}
        // label={
        //   <FormattedMessage
        //     id={labelValue(label || undefined, hideLabel, undefined)}
        //   />
        // }
        label={labelValue(label || undefined, hideLabel, undefined)}
        autoFocus={autofocus}
        required={required}
        disabled={disabled || readonly}
        {...otherProps}
        value={value || value === 0 ? value : ''}
        error={rawErrors.length > 0}
        onChange={onChangeOverride || _onChange}
        onBlur={_onBlur}
        onFocus={_onFocus}
        InputLabelProps={DisplayInputLabelProps}
        helperText={
          inputProps?.type === 'number' &&
          convertToWord &&
          Boolean(value) && (
            <Typography variant="caption" color="text.disabled">
              {numberToWords(Number(value))} {adornment}
            </Typography>
          )
        }
        {...textFieldProps}
        aria-describedby={ariaDescribedByIds(id, !!schema.examples)}
        slotProps={{
          input: {
            startAdornment: adornment && (
              <InputAdornment position="start" sx={{ px: 0.5 }}>
                <Typography variant="caption">{adornment}</Typography>
              </InputAdornment>
            ),
          },
        }}
        InputProps={{
          sx: {
            p1: 2,
          },
        }}
      />
      {Array.isArray(schema.examples) && (
        <datalist id={examplesId(id)}>
          {schema.examples
            .concat(schema.default && !schema.examples.includes(schema.default) ? [schema.default] : [])
            .map((example) => {
              return <option key={example} value={example} />;
            })}
        </datalist>
      )}
    </>
  );
}
